<template>
  <div
    v-if="enabled"
    class="discord"
  >
    <div
      v-show="isOpen"
      :id="panelId"
      class="discord__panel"
      role="dialog"
      aria-label="Chat Discord de We Are PlayStation"
    >
      <div class="discord__head">
        <svg
          class="discord__logo"
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <path :d="DISCORD_LOGO" />
        </svg>
        <div class="discord__title">
          <strong>We Are PlayStation</strong>
          <span>Serveur Discord</span>
        </div>
        <a
          class="discord__open"
          :href="discordUrl"
          target="_blank"
          rel="noopener noreferrer"
        >
          Ouvrir dans Discord
        </a>
        <button
          ref="closeButton"
          type="button"
          class="discord__close"
          aria-label="Fermer le chat Discord"
          @click="close"
        >
          ×
        </button>
      </div>
      <!-- loaded on the first opening only, then kept so the chat stays where the Waper left it -->
      <iframe
        v-if="loaded"
        class="discord__embed"
        :src="embedUrl"
        title="Chat Discord de We Are PlayStation"
        allow="clipboard-write; fullscreen"
      />
    </div>
    <button
      ref="bubble"
      type="button"
      class="discord__bubble"
      :class="{ 'is-open': isOpen }"
      :aria-label="
        isOpen ? 'Fermer le chat Discord' : 'Ouvrir le chat Discord de WAP'
      "
      :aria-expanded="isOpen"
      :aria-controls="panelId"
      @click="toggle"
    >
      <svg
        v-if="!isOpen"
        viewBox="0 0 24 24"
        aria-hidden="true"
      >
        <path :d="DISCORD_LOGO" />
      </svg>
      <span
        v-else
        aria-hidden="true"
        >×</span
      >
    </button>
    <span
      v-if="!isOpen"
      class="discord__tip"
      aria-hidden="true"
    >
      Rejoindre le Discord WAP
    </span>
  </div>
</template>

<script setup lang="ts">
/**
 * Discord chat of the WAP server. Replaces the WidgetBot "Crate" script, which ran eval() in our pages
 * (blocked by the Content-Security-Policy, see server/plugins/csp.ts) and loaded on every page view:
 * this button only opens the same WidgetBot embed in an iframe, on the first click.
 */
const DISCORD_LOGO =
  'M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028c.462-.63.874-1.295 1.226-1.994a.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z'

const { discordServer, discordChannel } = useRuntimeConfig().public
const enabled = computed(() => !!discordServer && !!discordChannel)
const embedUrl = `https://e.widgetbot.io/channels/${discordServer}/${discordChannel}`
const discordUrl = `https://discord.com/channels/${discordServer}/${discordChannel}`
const panelId = 'discord-chat'

const isOpen = ref(false)
const loaded = ref(false)
const bubble = ref<HTMLButtonElement>()
const closeButton = ref<HTMLButtonElement>()

const open = async () => {
  loaded.value = true
  isOpen.value = true
  await nextTick()
  closeButton.value?.focus()
}

const close = async () => {
  isOpen.value = false
  // on phones the bubble is hidden while the chat is open: focus it once it is back
  await nextTick()
  bubble.value?.focus()
}

const toggle = () => (isOpen.value ? close() : open())

useEventListener('keydown', (event: KeyboardEvent) => {
  if (event.key === 'Escape' && isOpen.value) close()
})
</script>

<style lang="scss" scoped>
$discord: #5865f2;
$discord-dark: #4752c4;
$discord-bg: #313338;

.discord__bubble {
  position: fixed;
  right: 2rem;
  bottom: 2rem;
  z-index: 99999; // under the cookie banner
  display: grid;
  place-items: center;
  width: 5.6rem;
  height: 5.6rem;
  padding: 0;
  color: $white;
  font-size: 2.8rem;
  line-height: 1;
  background: $discord;
  border: 0;
  border-radius: 50%;
  box-shadow: 0 0.6rem 1.6rem rgba(40, 50, 140, 0.35);
  cursor: pointer;
  transition:
    transform 0.15s ease,
    background 0.15s ease;

  svg {
    width: 2.8rem;
    height: 2.8rem;
    fill: currentColor;
  }

  &:hover {
    background: $discord-dark;
    transform: translateY(-0.2rem);
  }

  &:focus-visible {
    outline: 0.2rem solid $white;
    outline-offset: 0.2rem;
    box-shadow: 0 0 0 0.5rem $discord;
  }

  &:hover + .discord__tip,
  &:focus-visible + .discord__tip {
    opacity: 1;
  }
}

.discord__tip {
  position: fixed;
  right: 8.6rem;
  bottom: 3.4rem;
  z-index: 99999;
  padding: 0.6rem 1rem;
  color: $white;
  font-size: 1.2rem;
  white-space: nowrap;
  background: #111827;
  border-radius: 0.6rem;
  opacity: 0;
  transition: opacity 0.15s;
  pointer-events: none;
}

.discord__panel {
  position: fixed;
  right: 2rem;
  bottom: 9rem;
  z-index: 99999;
  display: flex;
  flex-direction: column;
  width: 36rem;
  height: min(56rem, calc(100vh - 12rem));
  overflow: hidden;
  background: $discord-bg;
  border-radius: 1.2rem;
  box-shadow: 0 1.4rem 4rem rgba(0, 0, 0, 0.35);
}

.discord__head {
  display: flex;
  gap: 1rem;
  align-items: center;
  padding: 1rem 1.2rem;
  color: $white;
  background: $discord;
}

.discord__logo {
  flex: none;
  width: 2.2rem;
  height: 2.2rem;
  fill: currentColor;
}

.discord__title {
  display: flex;
  flex-direction: column;
  min-width: 0;
  line-height: 1.2;

  strong {
    font-size: 1.3rem;
  }

  span {
    font-size: 1.1rem;
    opacity: 0.85;
  }
}

.discord__open {
  margin-left: auto;
  padding: 0.3rem 0.7rem;
  color: $white;
  font-size: 1.1rem;
  white-space: nowrap;
  text-decoration: none;
  border: 1px solid rgba(255, 255, 255, 0.5);
  border-radius: 0.4rem;

  &:hover {
    background: rgba(255, 255, 255, 0.12);
  }
}

.discord__close {
  width: 2.6rem;
  height: 2.6rem;
  padding: 0;
  color: $white;
  font-size: 1.8rem;
  line-height: 1;
  background: rgba(255, 255, 255, 0.15);
  border: 0;
  border-radius: 0.6rem;
  cursor: pointer;
}

.discord__open:focus-visible,
.discord__close:focus-visible {
  outline: 0.2rem solid $white;
  outline-offset: 0.2rem;
}

.discord__embed {
  flex: 1;
  width: 100%;
  min-height: 0;
  border: 0;
}

// phones: the chat takes the whole screen, closed from its header
@media screen and (max-width: 700px) {
  .discord__bubble {
    right: 1.4rem;
    bottom: 1.4rem;
    width: 5.2rem;
    height: 5.2rem;

    &.is-open {
      display: none;
    }
  }

  .discord__tip {
    display: none;
  }

  .discord__panel {
    inset: 0;
    width: auto;
    height: auto;
    padding-bottom: env(safe-area-inset-bottom, 0);
    border-radius: 0;
  }
}

@media (prefers-reduced-motion: reduce) {
  .discord__bubble,
  .discord__tip {
    transition: none;
  }
}
</style>
